import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { UpgradeModalProvider } from "@/components/UpgradeModal";
import { MAINTENANCE_MODE } from "@/lib/maintenance";
import { PRIVATE_MODE, hasPrivateAccess } from "@/lib/privateMode";
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";
const Maintenance = lazy(() => import("./pages/Maintenance"));
const Private = lazy(() => import("./pages/Private"));

// Lazy-loaded pages — keeps initial bundle small
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const WhatYouGet = lazy(() => import("./pages/WhatYouGet"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Examples = lazy(() => import("./pages/Examples"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Legal = lazy(() => import("./pages/Legal"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Refunds = lazy(() => import("./pages/Refunds"));
const Create = lazy(() => import("./pages/Create"));
const Upload = lazy(() => import("./pages/Upload"));
const Preview = lazy(() => import("./pages/Preview"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Auth = lazy(() => import("./pages/Auth"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const ResetSuccess = lazy(() => import("./pages/ResetSuccess"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Account = lazy(() => import("./pages/Account"));
const Contact = lazy(() => import("./pages/Contact"));
const GardenGallery = lazy(() => import("./pages/GardenGallery"));
const SubmitGarden = lazy(() => import("./pages/SubmitGarden"));
const NativePlantFinder = lazy(() => import("./pages/NativePlantFinder"));
const NativePlantsState = lazy(() => import("./pages/NativePlantsState"));
const NativePlantsCategory = lazy(() => import("./pages/NativePlantsCategory"));
const Pro = lazy(() => import("./pages/Pro"));
const ProDashboard = lazy(() => import("./pages/ProDashboard"));
const ProProject = lazy(() => import("./pages/ProProject"));
const PlantDirectory = lazy(() => import("./pages/PlantDirectory"));
const PlantDetail = lazy(() => import("./pages/PlantDetail"));
const PlantMap = lazy(() => import("./pages/PlantMap"));
const NativePlantsCity = lazy(() => import("./pages/NativePlantsCity"));
const NationalGardenMap = lazy(() => import("./pages/NationalGardenMap"));
const CommunityGardenMap = lazy(() => import("./pages/CommunityGardenMap"));
const CompatibilityChecker = lazy(() => import("./pages/CompatibilityChecker"));
const InvasiveReplacer = lazy(() => import("./pages/InvasiveReplacer"));
const Admin = lazy(() => import("./pages/Admin"));
const Disclaimer = lazy(() => import("./pages/Disclaimer"));
const ImageCredits = lazy(() => import("./pages/ImageCredits"));
const EcosystemDashboard = lazy(() => import("./pages/EcosystemDashboard"));
const NativeAlternativePage = lazy(() => import("./pages/NativeAlternativePage"));
const HabitatCalculator = lazy(() => import("./pages/HabitatCalculator"));
const NativePlantsCityV2 = lazy(() => import("./pages/NativePlantsCityV2"));
const EdibleNativePlants = lazy(() => import("./pages/EdibleNativePlants"));
const Blog = lazy(() => import("./pages/Blog"));

const queryClient = new QueryClient();

/** Scroll to top on every route change — fixes footer-link scroll carryover */
const ScrollToTop = () => {
  const location = useLocation();
  useEffect(() => {
    // Skip if user is intentionally targeting an in-page anchor (#hash).
    if (location.hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.hash]);
  return null;
};

/** Gate that redirects to /private when PRIVATE_MODE is on and no session access */
const PrivateGate = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (PRIVATE_MODE && !hasPrivateAccess() && location.pathname !== "/private") {
      navigate("/private", { replace: true });
    }
  }, [location.pathname, navigate]);

  if (PRIVATE_MODE && !hasPrivateAccess() && location.pathname !== "/private") {
    return null;
  }

  return <>{children}</>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthProvider>
          <UpgradeModalProvider>
          <ScrollToTop />
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-muted-foreground">Loading…</div></div>}>
          <PrivateGate>
          <Routes>
            <Route path="/private" element={<Private />} />
            {MAINTENANCE_MODE ? (
              <>
                <Route path="/" element={<Maintenance />} />
                <Route path="/create" element={<Maintenance />} />
                <Route path="/preview" element={<Maintenance />} />
                <Route path="/pricing" element={<Maintenance />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </>
            ) : (
              <>
            <Route path="/" element={<Home />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/what-you-get" element={<WhatYouGet />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/examples" element={<Examples />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/legal" element={<Legal />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/refunds" element={<Refunds />} />
            <Route path="/create" element={<Create />} />
            <Route path="/upload" element={<Upload />} />
            <Route path="/preview" element={<Preview />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/reset-success" element={<ResetSuccess />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/account" element={<Account />} />
            <Route path="/pro" element={<Pro />} />
            <Route path="/pro/dashboard" element={<ProDashboard />} />
            <Route path="/pro/project/:id" element={<ProProject />} />
            <Route path="/plants" element={<PlantDirectory />} />
            <Route path="/plants/:slug" element={<PlantDetail />} />
            <Route path="/plant-map" element={<PlantMap />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/national-garden-map" element={<NationalGardenMap />} />
            <Route path="/community-garden-map" element={<CommunityGardenMap />} />
            <Route path="/garden-gallery" element={<GardenGallery />} />
            <Route path="/submit-garden" element={<SubmitGarden />} />
            <Route path="/native-plant-finder" element={<NativePlantFinder />} />
            <Route path="/native-plants-:cityState" element={<NativePlantsCity />} />
            <Route path="/native-plants-for-:category" element={<NativePlantsCategory />} />
            <Route path="/compatibility-checker" element={<CompatibilityChecker />} />
            <Route path="/invasive-replacer" element={<InvasiveReplacer />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/image-credits" element={<ImageCredits />} />
            <Route path="/ecosystem" element={<EcosystemDashboard />} />
            <Route path="/habitat-calculator" element={<HabitatCalculator />} />
            <Route path="/native-alternative-to-:slug" element={<NativeAlternativePage />} />
            <Route path="/native-plants/:slug" element={<NativePlantsCityV2 />} />
            <Route path="/edible-native-plants" element={<EdibleNativePlants />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/admin" element={<Admin />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
              </>
            )}
          </Routes>
          </PrivateGate>
          </Suspense>
          </UpgradeModalProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
